import { Clock, DollarSign, FolderOpen, Hash, Package, RotateCcw, Search } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ServicePriceDisplay } from "@/domains/pricing/components/ServicePriceDisplay";
import type { ServiceCategory } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useRestoreService, useRestoreServiceCategory } from "../../hooks/use-services-mutation";
import type { ServiceRowData } from "../../types";

interface ArchiveManagerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salonId: string;
  archivedCategories: ServiceCategory[];
  archivedServicesByCategory: Record<string, ServiceRowData[]>;
  allArchivedServices: any[];
}

interface ConfirmationDialog {
  open: boolean;
  type: "restore-category" | "restore-service";
  item: ServiceCategory | ServiceRowData | null;
}

export function ArchiveManagerSheet({
  open,
  onOpenChange,
  salonId,
  archivedCategories,
  archivedServicesByCategory,
  allArchivedServices,
}: ArchiveManagerSheetProps) {
  const { t } = useTranslation("services");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("categories");
  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialog>({
    open: false,
    type: "restore-category",
    item: null,
  });

  const currency = useSalonStore((state) => state.currentSalon?.currency);

  // Mutation hooks
  const { execute: restoreCategory, isLoading: restoringCategory } = useRestoreServiceCategory();
  const { execute: restoreService, isLoading: restoringService } = useRestoreService();

  // Use the pre-processed archived services with proper category info

  // Filter archived categories based on search
  const filteredArchivedCategories = React.useMemo(() => {
    if (!searchTerm.trim()) return archivedCategories;
    const term = searchTerm.toLowerCase();
    return archivedCategories.filter(
      (category) =>
        category.name.toLowerCase().includes(term) ||
        category.description?.toLowerCase().includes(term)
    );
  }, [archivedCategories, searchTerm]);

  // Filter archived services based on search
  const filteredArchivedServices = React.useMemo(() => {
    if (!searchTerm.trim()) return allArchivedServices;
    const term = searchTerm.toLowerCase();
    return allArchivedServices.filter(
      (service) =>
        service.name.toLowerCase().includes(term) ||
        service.description?.toLowerCase().includes(term) ||
        service.categoryName.toLowerCase().includes(term) ||
        service.serviceCode.toLowerCase().includes(term)
    );
  }, [allArchivedServices, searchTerm]);

  const handleConfirmAction = async () => {
    if (!confirmDialog.item) return;

    try {
      switch (confirmDialog.type) {
        case "restore-category":
          await restoreCategory(salonId, confirmDialog.item.id);
          break;
        case "restore-service":
          await restoreService(salonId, confirmDialog.item.id);
          break;
      }
    } finally {
      setConfirmDialog({ open: false, type: "restore-category", item: null });
    }
  };

  const isLoading = restoringCategory || restoringService;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Archive
            </SheetTitle>
            <SheetDescription>
              Manage your archived categories and services. Restore items to make them active again.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col space-y-4 flex-1 overflow-hidden">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search archived items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Mobile Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex-1 overflow-hidden flex flex-col"
            >
              <TabsList className="grid w-full grid-cols-2 p-1">
                <TabsTrigger value="categories" className="flex items-center gap-2 text-xs">
                  <FolderOpen className="w-3 h-3" />
                  Categories ({filteredArchivedCategories.length})
                </TabsTrigger>
                <TabsTrigger value="services" className="flex items-center gap-2 text-xs">
                  <Package className="w-3 h-3" />
                  Services ({filteredArchivedServices.length})
                </TabsTrigger>
              </TabsList>

              {/* Categories Tab */}
              <TabsContent value="categories" className="flex-1 overflow-hidden">
                {filteredArchivedCategories.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FolderOpen className="w-16 h-16 text-muted-foreground/30 mb-4" />
                    <h3 className="font-medium text-foreground mb-2">
                      {searchTerm ? "No categories found" : "No archived categories"}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {searchTerm
                        ? "Try adjusting your search terms"
                        : "Archived categories will appear here"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 overflow-auto flex-1">
                    {filteredArchivedCategories.map((category) => {
                      const categoryServices = archivedServicesByCategory[category.id] || [];
                      const serviceCount = categoryServices.length;

                      return (
                        <div key={category.id} className="border rounded-lg p-4 bg-background">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <div
                                className="w-4 h-4 rounded-full border mt-0.5 flex-shrink-0"
                                style={{ backgroundColor: category.color }}
                              />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-base leading-tight">
                                  {category.name}
                                </h4>
                                {category.description && (
                                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                                    {category.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    {t("stats.services", { count: serviceCount })}
                                  </Badge>
                                  <Badge variant="destructive" className="text-xs">
                                    Archived
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setConfirmDialog({
                                  open: true,
                                  type: "restore-category",
                                  item: category,
                                })
                              }
                              disabled={isLoading}
                              className="ml-2 text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 hover:border-green-300 flex-shrink-0"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Services Tab */}
              <TabsContent value="services" className="flex-1 overflow-hidden">
                {filteredArchivedServices.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Package className="w-16 h-16 text-muted-foreground/30 mb-4" />
                    <h3 className="font-medium text-foreground mb-2">
                      {searchTerm ? "No services found" : "No archived services"}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {searchTerm
                        ? "Try adjusting your search terms"
                        : "Archived services will appear here"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 overflow-auto flex-1">
                    {filteredArchivedServices.map((service) => (
                      <div key={service.id} className="border rounded-lg p-4 bg-background">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-base leading-tight pr-2">
                                {service.name}
                              </h4>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setConfirmDialog({
                                    open: true,
                                    type: "restore-service",
                                    item: service,
                                  })
                                }
                                disabled={isLoading}
                                className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 hover:border-green-300 flex-shrink-0"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restore
                              </Button>
                            </div>

                            {service.description && (
                              <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                                {service.description}
                              </p>
                            )}

                            {/* Service Details */}
                            <div className="space-y-2">
                              <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-1.5">
                                  <Hash className="w-3 h-3 text-muted-foreground" />
                                  <Badge variant="outline" className="text-xs font-mono">
                                    {service.serviceCode}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">
                                    {service.duration} min
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: service.categoryColor }}
                                  />
                                  <span className="text-sm text-muted-foreground">
                                    {service.categoryName}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <DollarSign className="w-3 h-3 text-muted-foreground" />
                                  <ServicePriceDisplay
                                    mode={service.pricingMode}
                                    basePrice={service.basePrice}
                                    minPrice={service.minPrice}
                                    maxPrice={service.maxPrice}
                                    currency={currency}
                                    showBadge={false}
                                    showOnlyPrice={true}
                                    className="text-sm font-medium"
                                  />
                                </div>
                              </div>

                              <div className="flex items-center">
                                <Badge variant="destructive" className="text-xs">
                                  Archived
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      {/* Confirmation Dialog - Shared with Desktop */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Restore {confirmDialog.type.includes("category") ? "Category" : "Service"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.type === "restore-category" && (
                <>
                  Are you sure you want to restore the category "{confirmDialog.item?.name}"?
                  <span className="block mt-2">
                    The category and all its services will be moved back to active status and will
                    appear at the bottom of your active categories list.
                  </span>
                </>
              )}
              {confirmDialog.type === "restore-service" && (
                <>
                  Are you sure you want to restore the service "{confirmDialog.item?.name}"?
                  <span className="block mt-2">
                    The service will be moved back to active status and will be available for
                    booking again. It will appear at the bottom of your active services list.
                  </span>
                  <span className="block mt-2 text-sm text-muted-foreground">
                    Note: If the service's category is also archived, it will be automatically
                    restored as well.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isLoading ? "Restoring..." : "Restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
